import React, { useEffect, useState } from "react";
import SubscriptionPlan from "./SubscriptionPlan";
import SubscriptionUsage from "./SubscriptionUsage";
import {
  useSubscriptionStore,
  subscriptions,
} from "../../../state/useSubscriptionStore";
import CancelSubscriptionPrompt from "./cancelSubscriptionPrompt";
import {
  getSubscriptionHistory,
  getActiveSubscription,
} from "../../../backend-services/subscription";
import SubscriptionHistory from "./SubscriptionHistory";
import PageLoader from "../../common/PageLoader";
import { CloseIcon } from "../../../assets/icons";
import { InfoCircle } from "../../../assets/icons/InfoCircle";
import "react-loading-skeleton/dist/skeleton.css";
import { Active } from "../../../constants/Subscription";
import { formatMoney } from "../../../utils";
import { formattedDate } from "../../../utils/formatDate";
import { useUserStore } from "../../../state";
import { useTranslation } from "react-i18next";

interface Props {
  submenuTab: string;
  onBack: () => void;
  bankSubmenuTab: string;
}

const Subscription = (props: Props) => {
  const { t } = useTranslation("subscription");
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowProcessing, setIsShowProcessing] = useState(false);
  const [subscriptionState, setSubscriptionState] =
    useState<subscriptions | null>(null);
  const [history, setHistory] = useState([]);
  const [activeSub, setActiveSub] = useState(true);
  const { subscriptionData, setSubscriptionData } = useSubscriptionStore();
  const fetchActiveSubscription = useSubscriptionStore(
    (state) => state.fetchActiveSubscription,
  );
  const [error, setError] = React.useState("");
  

  const getHistoryData = async () => {
    setIsLoading(true);
    try {
      const data = await getSubscriptionHistory();
      const updatedHistory = data.map((history: any) => {
        return {
          costPerMonth: formatMoney(history.costPerMonth),
          startDate: formattedDate(history.startDate),
          endDate: formattedDate(history.endDate),
          plan: history.plan,
          status: history.status,
        };
      });
      setHistory(updatedHistory);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(true);
      throw new Error(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleActiveSubscription = React.useCallback(async () => {
  //   if (!(Object.keys(subscriptionData)?.length > 0)) {
  //     try {
  //       setIsLoading(true);
  //       const userSubHistory = await fetchActiveSubscription();
  //       setSubscriptionData(userSubHistory);
  //       setIsLoading(false);
  //     } catch (error: any) {
  //       console.error("Failed to fetch active subscription:", error);
  //       setError("Failed to load subscription data.");
  //     }
  //   }
  // }, []);

  const handleActiveSubscription = React.useCallback(async () => {
    if (!subscriptionData || Object.keys(subscriptionData).length === 0) {
      try {
        setIsLoading(true);
        const userSubHistory = await fetchActiveSubscription();
        setSubscriptionData(userSubHistory || {}); // Ensure userSubHistory is not null/undefined
        setIsLoading(false);
      } catch (error: any) {
        console.error("Failed to fetch active subscription:", error);
        setError("Failed to load subscription data.");
      }
    }
  }, [subscriptionData, fetchActiveSubscription, setSubscriptionData]);


  useEffect(() => {
    handleActiveSubscription();
    fetchActiveSubscription();
  }, []);

  useEffect(() => {
    getHistoryData();
  }, [activeSub]);

  const handleSubscriptionDeleteSuccess = () => {
    setShowPrompt(false);
  };

  useEffect(() => {
    if (subscriptionData?.status === "processing") {
      setIsShowProcessing(true);
    }
  }, [subscriptionData]);

  return (
    <>
      {isLoading && !subscriptionState ? (
        <PageLoader />
      ) : (
        <div>
          {subscriptionState?.status === "processing" && isShowProcessing && (
            <div className="absolute top-[-18px] left-0">
              <div className="hidden md:flex justify-between items-center w-1/2 xl:w-[834px] h-[72px] p-4 bg-gray-100 border rounded-lg">
                <div className="flex flex-row gap-[16px] items-center">
                  <InfoCircle />
                  <div className="word whitespace-nowrap">
                    <p>Your subscription is in the works, hold tight.</p>
                  </div>
                </div>

                <button
                  onClick={() => setIsShowProcessing(false)}
                  className="ml-4 text-gray-600 hover:text-gray-800"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col  w-full">
            <div className="bg-[#FFFFFF]  min-h-screen  rounded-[7px] ">
              <div className="flex flex-col gap-[7px]">
                <div className=" ">
                  <div className="hidden  md:flex justify-between items-center">
                    <span className="flex flex-col gap-[7px]">
                      <h3 className="text-[16px] md:text-[1.25rem] text-[#00000] leading-6  font-semibold">
                        {/* {t("subscription.title1")}  */}
                        Subscription
                      </h3>
                      {/* <p className="text-[12px] md:text-[1rem] md:leading-5  font-normal">
                        {t("subscription.manage")}
                      </p> */}
                    </span>
                    {subscriptionData?.status === Active && (
                      <div
                        onClick={() => setShowPrompt(true)}
                        className="w-[110px] h-[40px] hidden md:flex md:h-[48px] md:w-[195px]  justify-center items-center text-[14px] md:text-[16px] text-[#FB0303] font-semibold border cursor-pointer border-[#FB0303] rounded-[4px]"
                      >
                        Cancel plan
                      </div>
                    )}
                  </div>
                </div>
                <hr className="mt-[13px] mb-[24px] md:mb-[12px] hidden md:flex" />
                <div className="flex flex-col gap-[29px] lg:flex-row justify-between w-full px-[8px] ">
                  <SubscriptionPlan subscriptionState={subscriptionData}   showPrompt={showPrompt} setShowPrompt={setShowPrompt}  />
                  <SubscriptionUsage subscriptionState={subscriptionData} />
                </div>
              </div>
              <div className=" pt-[26px] md:pt-[44px]">
                <h3 className="text-[16px] md:text-[1.25rem]  ml-2  text-[#00000] leading-6  font-semibold">
                  {t("subscription.history")}
                </h3>
                <SubscriptionHistory isLoading={isLoading} history={history} />
              </div>
            </div>
          </div>

          {showPrompt && (
            <>
              <CancelSubscriptionPrompt
                handleGetActiveSubscription={handleActiveSubscription}
                id={subscriptionData.id}
                setShowPrompt={setShowPrompt}
                onClose={() => setShowPrompt(false)}
                onDeleteSuccess={handleSubscriptionDeleteSuccess}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Subscription;
